export type RipSound =
  | "pack_flyin"
  | "pack_pop"
  | "cards_fan"
  | "card_turn"
  | "sweat_whoosh"
  | "win1"
  | "win2"
  | "win3"
  | "win4"
  | "rainbow_hit";

const sprites: Record<RipSound, { start: number; duration: number }> = {
  pack_flyin: { start: 0, duration: 2 },
  pack_pop: { start: 2.25, duration: 3.004 },
  cards_fan: { start: 5.504, duration: 1.567 },
  card_turn: { start: 7.321, duration: 1 },
  sweat_whoosh: { start: 8.571, duration: 1 },
  win1: { start: 9.821, duration: 1.567 },
  win2: { start: 11.639, duration: 1.567 },
  win3: { start: 13.456, duration: 1.567 },
  win4: { start: 15.273, duration: 1.567 },
  rainbow_hit: { start: 20.841, duration: 1.933 },
};

const cashSprites = {
  listing_sold: { start: 4.863, duration: 1.043 },
  money_tick: { start: 6.006, duration: 1.565 },
  ui_toggle: { start: 17.567, duration: 0.13 },
} as const;

type CashSound = keyof typeof cashSprites;
type SpriteKit = "rip" | "cash";
type AudioRequest = (
  input: RequestInfo | URL,
) => Promise<Pick<Response, "ok" | "arrayBuffer">>;

let context: AudioContext | null = null;
const bufferPromises: Partial<Record<SpriteKit, Promise<AudioBuffer | null>>> =
  {};

function audioContext() {
  if (!context) {
    const AudioContextClass =
      window.AudioContext ??
      (
        window as Window & {
          webkitAudioContext?: typeof AudioContext;
        }
      ).webkitAudioContext;
    if (AudioContextClass) context = new AudioContextClass();
  }
  return context;
}

export function audioCodecCandidates(
  basename: string,
  canPlayType: (mime: string) => string,
) {
  const candidates = [
    {
      mime: 'audio/ogg; codecs="opus"',
      url: `/app/static/audio/${basename}.ogg`,
    },
    {
      mime: 'audio/mp4; codecs="mp4a.40.2"',
      url: `/app/static/audio/${basename}.m4a`,
    },
    { mime: "audio/wav", url: `/app/static/audio/${basename}.wav` },
  ];
  const playable = candidates.filter(({ mime }) => canPlayType(mime) !== "");
  return (playable.length ? playable : candidates).map(({ url }) => url);
}

export async function decodeAudioFallback(
  ctx: Pick<AudioContext, "decodeAudioData">,
  candidates: string[],
  request: AudioRequest = fetch,
) {
  for (const url of candidates) {
    try {
      const response = await request(url);
      if (!response.ok) continue;
      return await ctx.decodeAudioData(await response.arrayBuffer());
    } catch {
      // A codec can be reported as supported and still fail decode in an
      // embedded WebView. Continue through the exact donor codec ladder.
    }
  }
  return null;
}

function preloadSprite(kit: SpriteKit) {
  if (bufferPromises[kit]) return bufferPromises[kit];
  const ctx = audioContext();
  if (!ctx) return Promise.resolve(null);
  const audio = document.createElement("audio");
  const basename =
    kit === "rip" ? "collectibles_rip_sprite.v3" : "collectibles_audio_sprite";
  bufferPromises[kit] = decodeAudioFallback(
    ctx,
    audioCodecCandidates(basename, (mime) => audio.canPlayType(mime)),
  );
  return bufferPromises[kit];
}

export function preloadRipAudio() {
  return preloadSprite("rip");
}

export async function playRipSound(
  sound: RipSound,
  volume = 1,
  playbackRate = 1,
) {
  const ctx = audioContext();
  const buffer = await preloadSprite("rip");
  if (!ctx || !buffer) return;
  if (ctx.state === "suspended") await ctx.resume().catch(() => undefined);
  const sprite = sprites[sound];
  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  source.buffer = buffer;
  source.playbackRate.value = playbackRate;
  gain.gain.value = Math.max(0, Math.min(1, volume));
  source.connect(gain).connect(ctx.destination);
  source.start(0, sprite.start, sprite.duration);
}

async function playCashCue(sound: CashSound, volume: number) {
  const ctx = audioContext();
  const buffer = await preloadSprite("cash");
  if (!ctx || !buffer) return;
  if (ctx.state === "suspended") await ctx.resume().catch(() => undefined);
  const sprite = cashSprites[sound];
  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  source.buffer = buffer;
  gain.gain.value = Math.max(0, Math.min(1, volume));
  source.connect(gain).connect(ctx.destination);
  source.start(0, sprite.start, sprite.duration);
}

export function ripWinCue(level: number): RipSound {
  const value = Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;
  if (value < 0.25) return "win1";
  if (value < 0.5) return "win2";
  if (value < 0.8) return "win3";
  return "win4";
}

export function playRipWin(level: number, volume = 1) {
  void playRipSound(ripWinCue(level), volume);
  if ("vibrate" in navigator) {
    navigator.vibrate(level >= 0.6 ? [25, 20, 45] : 24);
  }
}

/** Exact Club cash-register cue from the canonical base sprite. */
export function playCashSound() {
  void playCashCue("listing_sold", 0.55);
}

export function playCoinTick(index = 0) {
  if (index < 4) void playCashCue("money_tick", 0.22);
  if ("vibrate" in navigator) navigator.vibrate(12);
}

export function playKeepCue() {
  void playCashCue("ui_toggle", 0.4);
  if ("vibrate" in navigator) navigator.vibrate(10);
}

export function startRiser() {
  const ctx = audioContext();
  if (!ctx) return { stop: () => undefined };
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  const now = ctx.currentTime;
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(62, now);
  oscillator.frequency.exponentialRampToValueAtTime(190, now + 2.1);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.075, now + 0.3);
  gain.gain.exponentialRampToValueAtTime(0.14, now + 2.1);
  oscillator.connect(gain).connect(ctx.destination);
  oscillator.start();
  return {
    stop() {
      const end = ctx.currentTime + 0.12;
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(
        Math.max(gain.gain.value, 0.0001),
        ctx.currentTime,
      );
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      oscillator.stop(end);
    },
  };
}
